import { type FC, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../../../../components/Button/Button'
import { Spinner } from '../../../../components/Spinner/Spinner'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../../store/auth/authSlice'
import { addToast } from '../../../../store/toast/toastSlice'
import {
  useDeleteSchedulingEventTypeMutation,
  useDuplicateSchedulingEventTypeMutation,
  useGetSchedulingEventTypesQuery,
  useUpsertSchedulingEventTypeMutation,
} from '../../../../store/scheduling/scheduling'
import { type EventConfigInput, EventTypeAccess, type SchedulingEventType } from '../../../../models/Scheduling'
import { TrackEvent, useTracking } from '../../../../apps/professional-app/fa/common/tracking'
import { EventTypeList } from '../../../Scheduling/shared/EventTypeList'
import { PlacementChips } from '../../../Scheduling/shared/PlacementChips'
import { HostStateLabel } from '../../../Scheduling/shared/HostStateLabel'
import { StatusBanner } from '../../../Scheduling/shared/StatusBanner'
import { accountPageBanner, hostWarning, isOnAccountPage } from '../../../Scheduling/shared/eventTypeHostState'
import { eventTypeEditorPath } from '../../../Scheduling/shared/bookingNavigation'
import { EventTypesInfoTooltip } from '../../../Scheduling/shared/EventTypesInfoTooltip'
import { EventTypeCreateModal } from '../../../Scheduling/shared/EventTypeCreateModal'
import { EventTypeImpactDialog } from '../../../Scheduling/shared/EventTypeImpactDialog'
import { SettingsCard, SettingsRow, SettingsSection } from './SettingsSection'

const ROW_WARNING_COPY = {
  'needs-hosts': 'No hosts assigned',
  'no-eligible-host': 'No host can take bookings',
} as const

const bannerCopy = (notLiveCount: number): string =>
  notLiveCount === 1
    ? "1 event type on the account booking page isn't live. Clients can't book it."
    : `${notLiveCount} event types on the account booking page aren't live. Clients can't book them.`

export const SharedEventTypesSection: FC<{ readOnly: boolean; accountBookingEnabled: boolean }> = ({ readOnly, accountBookingEnabled }) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { trackingClient } = useTracking()
  const authenticatedUser = useAppSelector(selectSafeAuthenticatedUser)
  const accountId = authenticatedUser.accountId
  const { data: eventTypes, isLoading, isFetching } = useGetSchedulingEventTypesQuery({ accountId })
  const [upsertEventType, { isLoading: isCreating }] = useUpsertSchedulingEventTypeMutation()
  const [duplicateEventType, { isLoading: isDuplicating }] = useDuplicateSchedulingEventTypeMutation()
  const [deleteEventType, { isLoading: isDeleting }] = useDeleteSchedulingEventTypeMutation()
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [eventTypeToDelete, setEventTypeToDelete] = useState<SchedulingEventType | null>(null)
  const isSaving = isDuplicating || (isFetching && !isLoading)

  const accountEventTypes = (eventTypes ?? []).filter((eventType) => eventType.access === EventTypeAccess.ACCOUNT)
  const banner = accountPageBanner(accountEventTypes, accountBookingEnabled)

  const handleCreate = async (eventType: SchedulingEventType, eventConfig?: EventConfigInput): Promise<SchedulingEventType | null> => {
    const result = await upsertEventType({ accountId, eventType, eventConfig })
    if ('error' in result) {
      dispatch(addToast({ variant: 'error', title: 'Could not create the event type', messages: ['Please try again.'] }))
      return null
    }
    trackingClient.track({
      eventName: TrackEvent.SCHEDULING_EVENT_TYPE_CREATED,
      eventDetails: { eventTypeId: result.data.id, access: EventTypeAccess.ACCOUNT },
    })
    dispatch(addToast({ variant: 'success', title: 'Event type created', messages: [`${result.data.name} is ready to use.`] }))
    return result.data
  }

  const rowStatus = (eventType: SchedulingEventType) => {
    if (!isOnAccountPage(eventType, accountBookingEnabled)) {
      return null
    }
    const warning = hostWarning(eventType)
    if (warning !== 'needs-hosts' && warning !== 'no-eligible-host') {
      return null
    }
    return <HostStateLabel tone='warning'>{ROW_WARNING_COPY[warning]}</HostStateLabel>
  }

  const handleDuplicate = async (eventType: SchedulingEventType) => {
    const eventTypeId = eventType.id
    const result = await duplicateEventType({ accountId, eventTypeId })
    if ('error' in result) {
      dispatch(addToast({ variant: 'error', title: 'Could not duplicate the event type', messages: ['Please try again.'] }))
    } else {
      trackingClient.track({ eventName: TrackEvent.SCHEDULING_EVENT_TYPE_DUPLICATED, eventDetails: { access: eventType.access, eventTypeId } })
      dispatch(addToast({ variant: 'success', title: 'Event type duplicated', messages: [`${result.data.name} was created.`] }))
    }
  }

  const handleDelete = async () => {
    if (!eventTypeToDelete) {
      return
    }
    const result = await deleteEventType({ accountId, eventTypeId: eventTypeToDelete.id })
    if ('error' in result) {
      dispatch(addToast({ variant: 'error', title: 'Could not delete the event type', messages: ['Please try again.'] }))
      return
    }
    trackingClient.track({ eventName: TrackEvent.SCHEDULING_EVENT_TYPE_DELETED, eventDetails: { access: eventTypeToDelete.access } })
    dispatch(addToast({ variant: 'success', title: 'Event type deleted', messages: [`${eventTypeToDelete.name} was removed.`] }))
    setEventTypeToDelete(null)
  }

  return (
    <SettingsSection
      title='Shared event types'
      subtitle='Set up the event types your clients can book. Each person in your account chooses which ones they use.'
      titleAdornment={<EventTypesInfoTooltip />}
      isSaving={isSaving}
      action={
        !readOnly ? (
          <Button variant='primary' className='text-nowrap' onClick={() => setIsCreateModalOpen(true)}>
            <i className='bi bi-plus-circle me-1' />
            New event type
          </Button>
        ) : null
      }
    >
      {banner?.kind === 'some-not-live' ? <StatusBanner tone='warning'>{bannerCopy(banner.notLiveCount)}</StatusBanner> : null}
      {banner?.kind === 'none-live' ? (
        <StatusBanner tone='danger'>No event types are live on the account booking page. Clients can&apos;t book anything there.</StatusBanner>
      ) : null}
      <SettingsCard>
        {isLoading ? (
          <SettingsRow className='d-flex justify-content-center'>
            <Spinner />
          </SettingsRow>
        ) : (
          <EventTypeList
            eventTypes={accountEventTypes}
            readOnly={readOnly}
            rowFooter={(eventType) => <PlacementChips eventType={eventType} />}
            rowStatus={rowStatus}
            onEdit={(eventType) => navigate(eventTypeEditorPath(eventType.id, 'availability'))}
            onDuplicate={handleDuplicate}
            onDelete={setEventTypeToDelete}
          />
        )}
      </SettingsCard>
      {isCreateModalOpen ? (
        <EventTypeCreateModal
          access={EventTypeAccess.ACCOUNT}
          isCreating={isCreating}
          accountBookingEnabled={accountBookingEnabled}
          onClose={() => setIsCreateModalOpen(false)}
          onCreate={handleCreate}
        />
      ) : null}
      {eventTypeToDelete ? (
        <EventTypeImpactDialog
          variant='delete'
          eventType={eventTypeToDelete}
          isLoading={isDeleting}
          onConfirm={handleDelete}
          onCancel={() => setEventTypeToDelete(null)}
        />
      ) : null}
    </SettingsSection>
  )
}
